import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { first, some, sortBy, find } from 'lodash';
import { combineLatest, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { UrlBuilder } from '../core';
import { CategoriesStorage, Category } from '../documents';
import { InvestigatorUnreadReportsStorage } from '../investigator/investigator-unread-reports-storage';
import { Site, InvestigatorSitesStorage } from '../sites';
import { SiteInfo, UnreadInfoItem, EQueryInfo } from './extended-site-info';
import { InvestigatorDocumentsCounter } from '../documents/models/documents-counter';
import { containsCaseInsensitive } from '../core/search-functions';
import { UserPreferencesStorage } from '../user-preferences/user-preferences-storage';
import { InvestigatorEQueriesCountersStorage } from '../investigator/e-queries/investigator-e-queries-counters-storage';
import { EQueryCounter } from '../e-queries/models/e-query-counter';

@Component({
    selector: 'all-sites',
    template: require('./sites-list.component.html'),
    styles: [require('./sites-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SitesListComponent implements OnDestroy {

    public searchTerm = new FormControl('');
    public loadingComplete = false;
    public hasAccessToSites = this.sitesStorage.loadSites().pipe(map(sites => sites.length > 0));

    public get favSitesInfo() {
        return this.filterSites(true);
    }

    public get otherSitesInfo() {
        return this.filterSites(false);
    }

    public readonly siteSorterMenuItems: SiteSorter[] = [
        {
            title: 'Protocol',
            getter: (item: SiteInfo) => item.site.study.clientProtocol.toLowerCase()
        },
        {
            title: 'Sponsor',
            getter: (item: SiteInfo) => item.site.study.sponsor.toLowerCase()
        },
        {
            title: 'Highest Unread Count',
            getter: (item: SiteInfo) => -item.unreadInfo.reduce((sum, current) => sum + current.unreadCount, 0)
        }
    ];

    public get selectedSiteSorter() {
        return this.siteSorter;
    }

    public set selectedSiteSorter(value: SiteSorter) {
        if (!value) {
            return;
        }

        this.siteSorter = value;
        this.userPreferencesStorage.set(this.sortingMethodStorageKey, this.siteSorter.title);
    }

    public allSitesInfo: SiteInfo[] = null;

    private readonly sortingMethodStorageKey = 'InvestigatorDashboard_SitesList_Sorting_Method';
    private siteSorter: SiteSorter;
    private subscription: Subscription;

    constructor(
        private sitesStorage: InvestigatorSitesStorage,
        private urlBuilder: UrlBuilder,
        unreadReportsRegistry: InvestigatorUnreadReportsStorage,
        categoriesStorage: CategoriesStorage,
        changeDetectorRef: ChangeDetectorRef,
        private userPreferencesStorage: UserPreferencesStorage,
        investigatorEQueriesCountersStorage: InvestigatorEQueriesCountersStorage,
    ) {
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        const allUnread = unreadReportsRegistry.getFreshUnreadsStream();
        const sortingMethod$ = userPreferencesStorage.get(this.sortingMethodStorageKey);
        const eQueryCounters$ = investigatorEQueriesCountersStorage.refreshAllCounters();

        this.subscription = combineLatest(
            sitesStorage.loadSites(true),
            categoriesStorage.siteSpecificCategories,
            allUnread,
            searchTermFlow,
            sortingMethod$,
            eQueryCounters$
        ).pipe(
            map(([site, categories, unreadReportData, term, sortingMethod, eQueryCounters]: [Site[], Category[], InvestigatorDocumentsCounter[], string, string, EQueryCounter[]]) =>
                [this.getSiteInfo(site, categories, unreadReportData, term, eQueryCounters), sortingMethod]
            )
        ).subscribe(([siteInfo, sortingMethod]: [SiteInfo[], string]) => {
            this.allSitesInfo = siteInfo;
            this.restoreSelectedSiteSorter(sortingMethod);
            this.applySorter();
            changeDetectorRef.markForCheck();
            this.loadingComplete = true;
        });
    }
    public sortSites(item: SiteSorter) {
        this.selectedSiteSorter = item;
        this.applySorter();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private applySorter() {
        if (!this.selectedSiteSorter) {
            return;
        }

        this.allSitesInfo = sortBy(this.allSitesInfo, this.selectedSiteSorter.getter);
    }

    private filterSites(favorites: boolean): SiteInfo[] {
        const result = this.allSitesInfo ?
            this.allSitesInfo.filter(si => si.site.isFavorite === favorites)
            : [];

        return result;
    }

    private getSiteInfo(
        sites: Site[],
        categories: Category[],
        unreadReports: InvestigatorDocumentsCounter[],
        term: string,
        eQueryCounters: EQueryCounter[] | null,
    ): SiteInfo[] {
        const selectedSites = term ? sites.filter(site =>
            containsCaseInsensitive(site.siteNum + ' - ' + site.investigatorFullName, term) ||
            containsCaseInsensitive(site.study.sponsor, term) ||
            containsCaseInsensitive(site.investigatorFullName, term) ||
            containsCaseInsensitive(site.study.clientProtocol, term)) : sites;

        return selectedSites.map((site: Site) => {
            const siteUnreadReports = unreadReports.filter((x: InvestigatorDocumentsCounter) => x.siteId === site.siteId);
            const counters: UnreadInfoItem[] = [];
            const siteUnreadLoaded = some(unreadReports, u => u.siteId !== null);
            categories.forEach((category) => {
                const categoryDocs = siteUnreadReports.filter(r => r.rootCategoryId === category.categoryId);
                const unreadCount = categoryDocs.reduce((res, val) => res += val.totalUnread, 0);
                const totalDocs = categoryDocs.reduce((res, val) => res += val.totalDocs, 0);

                const urlCommands = category.isLabReport()
                    ? this.urlBuilder
                        .esa()
                        .site(site)
                        .labReports()
                        .url()
                    : this.getUrlForUnreadItem(site, category);

                const unreadItem: UnreadInfoItem = {
                    unreadCount: unreadCount,
                    url: urlCommands,
                    title: category.name,
                    totalDocs: totalDocs,
                    categoryId: category.categoryId
                };

                counters.push(unreadItem);
            });

            const eQueryInfo = eQueryCounters ? this.getEQueryInfo(eQueryCounters, site) : null;

            return {
                site: site,
                unreadInfo: counters,
                countersReady: siteUnreadLoaded,
                eQueryInfo,
                eQueryInfoReady: !!eQueryCounters,
            };
        });
    }

    private getUrlForUnreadItem(site: Site, category: Category): any[] {
        const urlDocuments = category.parent
            ? this.urlBuilder
                .esa()
                .site(site)
                .documents(category.parent)
                .category(category)
                .url()
            : this.urlBuilder
                .esa()
                .site(site)
                .documents(category)
                .url();

        return urlDocuments;
    }

    private restoreSelectedSiteSorter(sortingMethod: string) {
        this.siteSorter = this.siteSorterMenuItems.find(c => c.title === sortingMethod) || first(this.siteSorterMenuItems);
    }

    private getEQueryInfo(equeryCounters: EQueryCounter[], site: Site): EQueryInfo {
        const eQueryCounter = find(equeryCounters, ec => ec.key === site.siteId);
        return {
            unresolvedTotal: eQueryCounter
                ? eQueryCounter.totalUnresolved.size
                : 0,
            total: eQueryCounter
                ? eQueryCounter.total
                : 0,
            url: this.urlBuilder
                .esa()
                .site(site)
                .eQueries()
                .url(),
        };
    }
}

export interface SiteSorter {
    title: string;
    getter: (item: SiteInfo) => string | number;
}
